import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import c from 'classnames';
import { useSelector } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import Slider from '@wix/da-ds/pkg/Slider';
import ProfileFolderCard from '../ProfileFolderCard';
import AddNewFolderCard from '../AddNewFolderCard';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { filterOutEmpty } from '../../../../utils/gallection';
import { useGallectionFolders } from '../hooks/useGallectionFolders';
import { getCanCreateGalleryFolder } from '../../../../selectors/privs';
import { PapiGallection } from '@wix/da-papi-types';
import {
  getProfileOwnerUser,
  isCurrentUserProfileOwner,
} from '../../../../selectors/users';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './FolderSlider.scss';

const WAIT_FOR_SCROLL = 300;

export interface Props {
  className?: string;
  cardWidth?: number;
  folder?: PapiGallection | null;
  subfolder?: PapiGallection | null;
  unsetSelectedSubfolderId: () => void;
}

const FolderSlider: FC<Props> = ({
  className,
  cardWidth: cardWidthFromProps,
  folder,
  subfolder,
  unsetSelectedSubfolderId,
}) => {
  const canCreateFolder = useSelector(getCanCreateGalleryFolder);
  const isProfileOwner = useSelector(isCurrentUserProfileOwner);
  const profileOwnerUser = useSelector(getProfileOwnerUser);

  const sliderContentRef = useRef<HTMLDivElement | null>(null);
  const scrollNodeRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollToIndex = useRef<boolean>(true);
  const preservedFolderIdRef = useRef<number | null>(null);
  const preservedOffsetRef = useRef<number | null>(null);
  const wasFetchingPreviousRef = useRef<boolean>(false);
  const showIndicator = useRef<boolean>(false);

  const {
    hasMore,
    hasLess,
    fetchNext,
    fetchPrevious,
    isFetching,
    folders,
    selectedFolderId,
  } = useGallectionFolders();

  const foldersToRender = useMemo(() => {
    return isProfileOwner ? folders : filterOutEmpty(folders);
  }, [isProfileOwner, folders]);

  const isMobile = useContext(MobileContext);
  const cardWidth = useMemo(() => {
    return cardWidthFromProps
      ? cardWidthFromProps
      : parseInt(s['folder-card-width']);
  }, [cardWidthFromProps]);

  const scrollToIndex: number | null = useMemo(() => {
    if (shouldScrollToIndex.current) {
      return foldersToRender.findIndex(({ folderId }) => {
        return folderId === selectedFolderId;
      });
    } else {
      return null;
    }
  }, [foldersToRender, selectedFolderId]);

  const preserveScrollPosition = useCallback(() => {
    showIndicator.current = true;
    shouldScrollToIndex.current = false;
    const sliderRect = sliderContentRef.current?.getBoundingClientRect();
    const folderRect = scrollNodeRef.current?.getBoundingClientRect();

    if (sliderRect && folderRect) {
      preservedOffsetRef.current = folderRect.left - sliderRect.left;
    }
  }, []);

  const restoreScrollPosition = useCallback(() => {
    if (preservedFolderIdRef.current && preservedOffsetRef.current) {
      const folderNode = scrollNodeRef.current;
      const sliderNode = sliderContentRef.current;

      if (folderNode && sliderNode) {
        const sliderRect = sliderNode.getBoundingClientRect();
        const folderRect = folderNode.getBoundingClientRect();
        const currentOffset = folderRect.left - sliderRect.left;
        const scrollAmount = currentOffset - preservedOffsetRef.current;
        sliderNode.scrollTo({
          left: sliderNode.scrollLeft + scrollAmount,
          behavior: 'instant',
        });
        showIndicator.current = false;
      }
    }
  }, []);

  const fetchNextFunc = useCallback(() => {
    // Don't need to preserve scrolling when you are going forward
    // because when things are added to the end, just leaving the scroll
    // as is will already have it in the right position
    fetchNext();
  }, [fetchNext]);

  const fetchPrevFunc = useCallback(() => {
    wasFetchingPreviousRef.current = true;

    // Need to wait for the scrolling to finish before we can record our
    // position. Fetching cannot happen before we've recorded our position
    // because the server could respond faster than it takes to scroll
    setTimeout(() => {
      preserveScrollPosition();
      fetchPrevious();
    }, WAIT_FOR_SCROLL);
  }, [fetchPrevious, preserveScrollPosition]);

  useEffect(() => {
    if (!isFetching && wasFetchingPreviousRef.current) {
      wasFetchingPreviousRef.current = false;
      restoreScrollPosition();
    }
  }, [hasLess, isFetching, restoreScrollPosition]);

  return (
    <div className={s['wrapper']}>
      <Slider
        className={c(s['folders-slider'], isMobile && s['mobile'], className)}
        scrollToIndex={scrollToIndex}
        smoothInitialScroll={false}
        contentRef={sliderContentRef}
      >
        {hasLess && (
          <Waypoint onEnter={fetchPrevFunc} horizontal topOffset="-20%" />
        )}
        {foldersToRender.map((f, index) => {
          const shouldUseRef: boolean =
            preservedFolderIdRef.current !== null
              ? f.folderId === preservedFolderIdRef.current
              : f.folderId > 0;
          const onClick: (() => void) | null = null;
          let isSelected = false;
          const isParent: boolean =
            f.folderId === folder?.folderId && folder?.hasSubfolders;

          if (shouldUseRef) {
            preservedFolderIdRef.current = f.folderId;
          }

          if (folder && f.folderId === folder.folderId) {
            isSelected = true;
          }

          return (
            <ProfileFolderCard
              isSelected={isSelected}
              withPrice
              width={cardWidth}
              className={s['folder-card']}
              key={f.folderId}
              folder={f}
              indexInsideParent={index}
              withLink
              isProfileOwner={isProfileOwner}
              profileOwnerUser={profileOwnerUser}
              onClick={onClick}
              isParent={isParent}
              cardRef={shouldUseRef ? scrollNodeRef : undefined}
            />
          );
        })}
        {hasMore && (
          <Waypoint onEnter={fetchNextFunc} horizontal bottomOffset="-20%" />
        )}
        {canCreateFolder && (
          <AddNewFolderCard className={s['folder-card']} width={cardWidth} />
        )}
      </Slider>

      {showIndicator.current && (
        <LoadingIndicator className={s['loading-indicator']} />
      )}
    </div>
  );
};

FolderSlider.displayName = 'FolderSlider';
export default FolderSlider;
